import { z } from "zod/v4";

import {
  AgenticLogicRuleDependencyDescriptions,
  AgenticLogicRuleDraftDtoDescriptions,
  AgenticLogicRuleDtoDescriptions,
  AgenticLogicRuleSettingsDescriptions,
} from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";
import { AgenticCodegenOriginSchema } from "../../functions/agentic-codegen/provenance";

import { RuleGraphDtoSchema } from "./rule-graph";
import { AgenticLogicSurfaceSetSchema } from "./surface";

// ---------------------------------------------------------------------------
// Rule lifecycle vocabularies + the rule/draft admin DTOs — the
// persistence-facing sibling of the serving envelope in
// functions/agentic-logic/types. Everything here reaches storage or the
// internal/external admin APIs, so every list is a wire format: values are
// never renamed, only appended.
//
// Timestamps on these DTOs are Unix epoch-ms numbers (they mirror database
// rows), unlike the serving contract's ISO-8601 strings.
// ---------------------------------------------------------------------------

/**
 * Rule lifecycle states. Wire format — append-only.
 *
 * draft → active → paused, with resume going paused → active. A paused rule
 * renders as rule-absent on serving.
 *
 * Retiring a rule is DELETE, from any state and never while referenced — there
 * is no `archived` state and no archive/restore pair. The value is retired
 * from this append-only list rather than reused.
 */
export const AgenticLogicRuleStateList = ["draft", "active", "paused"] as const;
export const AgenticLogicRuleStateSchema = z.enum(AgenticLogicRuleStateList);
export type AgenticLogicRuleState = (typeof AgenticLogicRuleStateList)[number];

/**
 * Who manages a rule's implementation. Wire format — append-only.
 * `builder` rules are template configurations (templateId + params +
 * templateVersion); `code` rules own a custom-code workspace.
 */
export const AgenticLogicManagedByList = ["builder", "code"] as const;
export const AgenticLogicManagedBySchema = z.enum(AgenticLogicManagedByList);
export type AgenticLogicManagedBy = (typeof AgenticLogicManagedByList)[number];

/**
 * Built-in rules Intelligems provisions for every store. Wire format and a
 * storage key — append-only.
 *
 * A system rule is an ordinary builder rule plus this key: it is published and
 * served through the same pipeline as any other rule, so nothing downstream of
 * the rule row needs to know it is built in. What the key buys is identity (at
 * most one rule per key per store, enforced by a unique index) and ownership
 * (merchants cannot rename, edit, pause, resume or delete it).
 */
export const AgenticLogicSystemRuleKeyList = [
  "shopify-recommendations",
] as const;

export const AgenticLogicSystemRuleKeySchema = z.enum(
  AgenticLogicSystemRuleKeyList
);

export type AgenticLogicSystemRuleKey =
  (typeof AgenticLogicSystemRuleKeyList)[number];

/** Whether a rule is built in, and therefore read-only to merchants. */
export const isAgenticLogicSystemRule = (rule: {
  systemKey?: AgenticLogicSystemRuleKey | null;
}): boolean => rule.systemKey != null;

/**
 * What a surface does when the rule filling it returns zero items. Wire format
 * — append-only, so never resolve a conflict between two values by sorting
 * them. Defined ONCE here; the serving broker and the smart-recs fallback both
 * consume this list.
 */
export const AgenticLogicOnEmptyList = ["hide", "smart-recs"] as const;
export const AgenticLogicOnEmptySchema = z.enum(AgenticLogicOnEmptyList);
export type AgenticLogicOnEmpty = (typeof AgenticLogicOnEmptyList)[number];

/**
 * Most products a rule may return, on any surface.
 *
 * ONE bound and not a per-surface pair: a rule declares `targetSurfaces` and
 * can serve all three, so a bound that changed with the surface set would let
 * a legal rule become illegal by being widened. The checkout grid's bound is
 * the tightest of the three, so it is the one that holds everywhere.
 */
export const AGENTIC_LOGIC_MIN_MAX_ITEMS = 1;
export const AGENTIC_LOGIC_MAX_MAX_ITEMS = 10;

/**
 * The recommendation settings, on the RULE.
 *
 * These used to live on the container — `variables.maxProductLimit` and the two
 * exclusion booleans on a checkout block, the same three on an onsite
 * component's config, and `onEmpty` on the placement. A merchant asking "why is
 * this product not showing" therefore had to look in two places, and on a
 * migrated checkout block the container's copy was not even read at serve time.
 *
 * The container keeps its own fields for the LEGACY engine, which still serves
 * every container that is not bound to a rule. A bound container ignores them.
 *
 * Presentation stays behind: a checkout block's mobile/desktop grid paginates
 * the returned list into a carousel and never caps it, so it describes the
 * location rather than the selection and belongs with the placement.
 */
export const AgenticLogicRuleSettingsSchema = z
  .object({
    maxItems: z
      .number()
      .int()
      .min(AGENTIC_LOGIC_MIN_MAX_ITEMS)
      .max(AGENTIC_LOGIC_MAX_MAX_ITEMS)
      .describe(AgenticLogicRuleSettingsDescriptions.maxItems),
    excludeOutOfStock: z
      .boolean()
      .describe(AgenticLogicRuleSettingsDescriptions.excludeOutOfStock),
    excludeItemsInCart: z
      .boolean()
      .describe(AgenticLogicRuleSettingsDescriptions.excludeItemsInCart),
    onEmpty: AgenticLogicOnEmptySchema.describe(
      AgenticLogicRuleSettingsDescriptions.onEmpty
    ),
  })
  .describe(AgenticLogicRuleSettingsDescriptions._schema);

export type AgenticLogicRuleSettings = z.infer<
  typeof AgenticLogicRuleSettingsSchema
>;

/**
 * What a rule gets when nobody names a setting.
 *
 * Both exclusions default ON. That is what the template tier already did
 * (`AGENTIC_LOGIC_SHARED_FILTER_FIELDS` defaulted both true) and what the
 * legacy-upsell migration already forced with `?? true`; the checkout block's
 * `false` defaults were the outlier, and they were unread at serve time.
 * `hide` is the empty-result floor because it fabricates no content.
 */
export const AGENTIC_LOGIC_DEFAULT_RULE_SETTINGS: AgenticLogicRuleSettings = {
  maxItems: 3,
  excludeOutOfStock: true,
  excludeItemsInCart: true,
  onEmpty: "hide",
};

export const AgenticLogicRuleDtoSchema = z
  .object({
    id: z.string().describe(AgenticLogicRuleDtoDescriptions.id),
    organizationId: z
      .string()
      .describe(AgenticLogicRuleDtoDescriptions.organizationId),
    name: z.string().describe(AgenticLogicRuleDtoDescriptions.name),
    description: z
      .string()
      .trim()
      .min(1)
      .max(1024)
      .describe(AgenticLogicRuleDtoDescriptions.description),
    managedBy: AgenticLogicManagedBySchema.describe(
      AgenticLogicRuleDtoDescriptions.managedBy
    ),
    state: AgenticLogicRuleStateSchema.describe(
      AgenticLogicRuleDtoDescriptions.state
    ),
    templateId: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDtoDescriptions.templateId),
    templateParams: z
      .record(z.string(), z.unknown())
      .nullable()
      .describe(AgenticLogicRuleDtoDescriptions.templateParams),
    templateVersion: z
      .number()
      .int()
      .positive()
      .nullable()
      .describe(AgenticLogicRuleDtoDescriptions.templateVersion),
    targetSurfaces: AgenticLogicSurfaceSetSchema.nullable().describe(
      AgenticLogicRuleDtoDescriptions.targetSurfaces
    ),
    settings: AgenticLogicRuleSettingsSchema.describe(
      AgenticLogicRuleDtoDescriptions.settings
    ),
    authoringSessionId: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDtoDescriptions.authoringSessionId),
    // Nullish rather than nullable: absent reads exactly like null (a
    // merchant-owned rule), so a fixture or an older payload that predates the
    // built-in rules stays a valid merchant rule instead of failing to parse.
    systemKey: AgenticLogicSystemRuleKeySchema.nullish().describe(
      AgenticLogicRuleDtoDescriptions.systemKey
    ),
    createdAtTs: z
      .number()
      .describe(AgenticLogicRuleDtoDescriptions.createdAtTs),
    lastUpdateTs: z
      .number()
      .describe(AgenticLogicRuleDtoDescriptions.lastUpdateTs),
  })
  .describe(AgenticLogicRuleDtoDescriptions._schema);

export type AgenticLogicRuleDto = z.infer<typeof AgenticLogicRuleDtoSchema>;

export const AgenticLogicRuleDependencySchema = z
  .object({
    name: z
      .string()
      .min(1)
      .describe(AgenticLogicRuleDependencyDescriptions.name),
    version: z
      .string()
      .min(1)
      .describe(AgenticLogicRuleDependencyDescriptions.version),
  })
  .describe(AgenticLogicRuleDependencyDescriptions._schema);

export type AgenticLogicRuleDependency = z.infer<
  typeof AgenticLogicRuleDependencySchema
>;

/**
 * The dependency list a request may WRITE: empty, always.
 *
 * Agentic Logic admits no third-party package. The rule runtime's no-network,
 * no-environment, no-clock boundary is a set of bindings scoped to the rule's
 * own files, so a bundled package resolves the real `fetch`, the real
 * `process.env` and the real clock — it would run inside the rule with the
 * boundary drawn around nothing. The bundler enforces that when it builds; this
 * is the same rule stated where a caller first states its intent, so the API
 * cannot accept a package it is going to refuse to ship.
 *
 * Paired with {@link AgenticLogicStoredDependenciesSchema}, which is the same
 * list travelling the other way. The split is the point: a refusal belongs on
 * the path that would persist a package, and nowhere else.
 */
export const AgenticLogicWritableDependenciesSchema = z
  .array(AgenticLogicRuleDependencySchema)
  .max(
    0,
    "Agentic Logic rules run with no third-party npm packages. Read commerce state through the injected SDK instead."
  )
  .default([]);

/**
 * A rule's ALREADY-STORED dependency list, carried as read-only context.
 *
 * Empty for every rule saved under the current contract, and deliberately
 * tolerant of a legacy row that still holds one. The AI requests that take it
 * — repair, and the graph / scenario / test generators — only read it, so
 * refusing it would install nothing and prevent nothing; it would just blind
 * the model to a package the source it is being asked to fix still imports,
 * and make the rules that most need repair the only ones that cannot get it.
 *
 * {@link AgenticLogicRuleDraftDtoSchema} is tolerant for the same reason: a
 * refusal on the way in must not make an existing draft unreadable.
 */
export const AgenticLogicStoredDependenciesSchema = z
  .array(AgenticLogicRuleDependencySchema)
  .default([]);

export const AgenticLogicRuleDraftDtoSchema = z
  .object({
    ruleId: z.string().describe(AgenticLogicRuleDraftDtoDescriptions.ruleId),
    source: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.source),
    userTestSuite: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.userTestSuite),
    generatedTestSuite: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.generatedTestSuite),
    dependencies: z
      .array(AgenticLogicRuleDependencySchema)
      .describe(AgenticLogicRuleDraftDtoDescriptions.dependencies),
    updatedByUserId: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.updatedByUserId),
    updatedByUser: z
      .object({ name: z.string() })
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.updatedByUser),
    lastUpdateTs: z
      .number()
      .describe(AgenticLogicRuleDraftDtoDescriptions.lastUpdateTs),
    origin: AgenticCodegenOriginSchema.nullable().describe(
      AgenticLogicRuleDraftDtoDescriptions.origin
    ),
    graph: RuleGraphDtoSchema.nullable().describe(
      AgenticLogicRuleDraftDtoDescriptions.graph
    ),
    contentFingerprint: z
      .string()
      .nullable()
      .describe(AgenticLogicRuleDraftDtoDescriptions.contentFingerprint),
  })
  .describe(AgenticLogicRuleDraftDtoDescriptions._schema);

export type AgenticLogicRuleDraftDto = z.infer<
  typeof AgenticLogicRuleDraftDtoSchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicRuleDtoDescriptions,
  AgenticLogicRuleDto
>();
assertDescriptions<
  typeof AgenticLogicRuleDependencyDescriptions,
  AgenticLogicRuleDependency
>();
assertDescriptions<
  typeof AgenticLogicRuleDraftDtoDescriptions,
  AgenticLogicRuleDraftDto
>();
